import type { VNode } from "preact";
import { signal } from "@preact/signals";
import { store } from "~/core/store";
import { generateId } from "~/utils";
import type { RaiseSnackFn, Snack, SnackType } from "./interfaces";

const DEFAULT_TIMEOUT = 5000;
const ITEMS_DELAY = 150;

const makeSnack = () => {
    const items = signal<Snack[]>([]);
    const timeoutIds = new Map<Snack, number>();

    const remove = (snack: Snack) => {
        const newItems = items.value.slice();
        const index = newItems.indexOf(snack);

        if (index !== -1) {
            const tmId = timeoutIds.get(snack);

            if (tmId) {
                window.clearTimeout(tmId);
            }

            newItems.splice(index, 1);
            timeoutIds.delete(snack);
            items.value = newItems;
        }
    };

    const hold = () => {
        timeoutIds.forEach((value, key) => {
            window.clearTimeout(value);
            timeoutIds.delete(key);
        });
    };

    const unhold = () => {
        items.value.forEach((snack, i) => {
            timeoutIds.set(
                snack,
                window.setTimeout(
                    () => remove(snack),
                    snack.timeout + i * ITEMS_DELAY,
                ),
            );
        });
    };

    const push = (
        type: SnackType,
        titleOrContent: string | VNode,
        detailsOrTimeout?: string | number,
        timeout?: number,
    ) => {
        const id = generateId();
        const newItems = items.value.slice();
        let snack: Snack;

        if (type === "error") {
            store.mobile.generateImpact("heavy");
        }

        if (
            typeof detailsOrTimeout === "string" &&
            typeof titleOrContent === "string"
        ) {
            snack = {
                id,
                type,
                title: titleOrContent,
                details: detailsOrTimeout,
                timeout: timeout ?? DEFAULT_TIMEOUT,
            };
        } else {
            snack = {
                id,
                type,
                content: titleOrContent as VNode,
                timeout: (detailsOrTimeout as number) ?? DEFAULT_TIMEOUT,
            };
        }

        timeoutIds.set(
            snack,
            window.setTimeout(() => remove(snack), snack.timeout),
        );
        newItems.push(snack);
        items.value = newItems;
    };

    const success: RaiseSnackFn = (
        titleOrContent: string | VNode,
        detailsOrTimeout?: string | number,
        timeout?: number,
    ) => push("success", titleOrContent, detailsOrTimeout, timeout);

    const error: RaiseSnackFn = (
        titleOrContent: string | VNode,
        detailsOrTimeout?: string | number,
        timeout?: number,
    ) => push("error", titleOrContent, detailsOrTimeout, timeout);

    const info: RaiseSnackFn = (
        titleOrContent: string | VNode,
        detailsOrTimeout?: string | number,
        timeout?: number,
    ) => push("info", titleOrContent, detailsOrTimeout, timeout);

    const warning: RaiseSnackFn = (
        titleOrContent: string | VNode,
        detailsOrTimeout?: string | number,
        timeout?: number,
    ) => push("warning", titleOrContent, detailsOrTimeout, timeout);

    return {
        items,
        success,
        error,
        warning,
        info,
        remove,
        hold,
        unhold,
    };
};

export default makeSnack;
